import React from 'react';
import { formatPartnerTableDate } from '../../Utils/partnerDateUtils';
import { getCompanyRowKey } from '../../Utils/partnerCompanyUsersUtils';
import PartnerCompanyUsersTable from './PartnerCompanyUsersTable';

const PAE_NAME_SINGLE_LINE_MAX_LENGTH = 20;

const formatPaeName = (name) => {
  if (!name) {
    return '-';
  }

  const trimmed = name.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length <= 1 || trimmed.length <= PAE_NAME_SINGLE_LINE_MAX_LENGTH) {
    return trimmed;
  }

  const lastName = parts[parts.length - 1];
  const firstPart = parts.slice(0, -1).join(' ');

  return (
    <span className="partner-company-pae-name">
      <span className="partner-company-pae-line">{firstPart}</span>
      <span className="partner-company-pae-line">{lastName}</span>
    </span>
  );
};

const PartnerCompaniesTable = ({
  companies,
  expandedCompanyId,
  emptyMessage = 'No partner companies found.',
  onToggleExpand,
  onEdit,
}) => {
  if (!companies.length) {
    return (
      <div className="partner-table-container">
        <p className="partner-table-empty">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="partner-table-container partner-companies-table-wrap">
      <table className="table partner-table partner-companies-table">
        <thead>
          <tr>
            <th>CSN</th>
            <th>Partner Company</th>
            <th>Type</th>
            <th>PAE</th>
            <th>Tier Status</th>
            <th>Service Level</th>
            <th>Updated by</th>
            <th>Updated at</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((company) => {
            const rowKey = getCompanyRowKey(company);
            const users = company.users || [];
            const userCount = users.length;
            const isExpanded = expandedCompanyId === rowKey;

            return (
              <React.Fragment key={rowKey}>
                <tr className={isExpanded ? 'partner-company-row is-expanded' : 'partner-company-row'}>
                  <td>{company.csn || '-'}</td>
                  <td className="partner-company-name-cell">{company.companyName || '-'}</td>
                  <td className="partner-company-type-cell">
                    {company.typeOfPartner ? (
                      <span className="partner-company-type-badge">{company.typeOfPartner}</span>
                    ) : (
                      <span className="partner-company-empty-value">-</span>
                    )}
                  </td>
                  <td className="partner-company-pae-cell">{formatPaeName(company.partnerAdoptionEngineer)}</td>
                  <td className="partner-company-badge-cell">
                    {company.tierStatus ? (
                      <span className="partner-company-tier-badge">{company.tierStatus}</span>
                    ) : (
                      <span className="partner-company-empty-value">-</span>
                    )}
                  </td>
                  <td className="partner-company-badge-cell">
                    {company.serviceLevel ? (
                      <span className="partner-company-service-badge">{company.serviceLevel}</span>
                    ) : (
                      <span className="partner-company-empty-value">-</span>
                    )}
                  </td>
                  <td className="partner-company-meta-cell">{company.updatedBy || '-'}</td>
                  <td className="partner-company-meta-cell partner-company-updated-at-cell">
                    {formatPartnerTableDate(company.updatedAt)}
                  </td>
                  <td className="partner-company-actions-cell">
                    <div className="partner-company-actions-group">
                      <button
                        type="button"
                        className={`partner-company-partners-btn ${isExpanded ? 'is-active' : ''}`}
                        onClick={() => onToggleExpand(rowKey)}
                        aria-expanded={isExpanded}
                      >
                        <span className="partner-company-partners-btn-text">
                          Partners
                          <span className="partner-company-partners-count">({userCount})</span>
                        </span>
                        <span className="partner-company-expand-icon" aria-hidden="true">
                          {isExpanded ? '▴' : '▾'}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="partner-company-edit-btn"
                        onClick={() => onEdit(company)}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>

                {isExpanded ? (
                  <tr className="partner-company-users-row">
                    <td colSpan={9}>
                      <div className="partner-company-users-panel">
                        <p className="partner-company-users-title">
                          Partner users ({userCount})
                        </p>
                        <PartnerCompanyUsersTable users={users} />
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PartnerCompaniesTable;
